<!--
  Products（路由 /products）

  還原自 index.pretty.js L2900。render function 已轉回 template，
  async 方法已由 regenerator 狀態機還原為 async/await。

  菜單頁，也是整個點餐流程的樞紐：
    - 依分類列出飲品，圖片以 IntersectionObserver 延遲載入
    - 捲動時同步高亮上方的分類標籤
    - 掛載 VCart（購物車）、VProduct（客製化）、VLocation（取餐方式）三個對話框

  網址參數：
    ?q=<LINE user id>  由 LINE bot 導入時帶入
    ?d=<drink_id>      指定商品，載入後自動開啟該商品的客製化對話框
    ?o=yes / no        指定取餐方式（yes=自取、no=外送，外送但無地址則導回首頁）

  備餐時間公式：每 6 杯加 10 分鐘，再加上後端設定的基礎備餐時間。
-->

<template>
  <div id="wrapper" class="m-all-0 wrapper_flex">
    <div>
      <transition>
        <v-cart
          v-show="cart_show"
          ref="pop_cart"
          :is_close="is_close"
          :cart_list="cart_list"
          :delivery="delivery"
          :order_type="order_type"
          @event="trigger_event"
          @select="select_item_from_cart"
        />
      </transition>

      <v-header
        :is_close="is_close"
        :total_count="total_count"
        :cart_actived="cart_actived"
        :time="time"
        @event="trigger_event"
      />

      <div v-if="is_loading === false && menu !== null">
        <nav class="min-navbar collapse" :class="{ show: header_show }">
          <div class="nav-container">
            <div class="nav-content d-flex jc-space a-center">
              <p>微微糖・青埔形象店</p>
              <div class="nav-collapse d-flex">
                <button
                  v-if="cart_actived"
                  class="btn btn-primary btn-img-msg btn-round d-flex a-center as-flex-end m-r-8"
                  @click="trigger_cart"
                >
                  <i class="material-icons m-r-8">shopping_cart</i>
                  <p class="s-screen m-r-4">{{ $t('your_cart') }} • </p>
                  <p>{{ total_count }}</p>
                </button>
              </div>
            </div>
          </div>
        </nav>

        <div class="location-container d-flex a-center">
          <div
            class="location-content d-flex jc-space a-center"
            @click="trigger_event('set_location')"
          >
            <div v-if="order_type === 'ORDER_TYPE_DELIVERY'" class="max-width-85">
              <div class="location-label d-flex fd-column font-16">
                <p class="line-2">{{ $t('delivering_to') }}: {{ delivery.location }}</p>

                <div v-if="delivery.distance > 10" class="m-t-12 t-gray-7">
                  <p class="line-2">{{ $t('delivery_notes_2') }}</p>
                </div>
                <div v-else class="m-t-12 t-gray-7">
                  <p class="line-2">{{ $t('delivery_fee_notes', { cost: delivery.fee }) }}</p>
                </div>
              </div>
            </div>

            <div v-else class="d-flex max-width-85">
              <div class="location-label font-16">
                <p class="line-2">{{ $t('where') }}: {{ $t('location') }}</p>
              </div>
            </div>

            <i class="font-18 material-icons m-r-8 t-gray-7">
              <span class="material-icons">expand_more</span>
            </i>
          </div>

          <transition name="slide" />
        </div>

        <!-- 分類標籤列，捲動時會自動高亮並水平捲到對應位置 -->
        <div class="menu-container d-flex a-center">
          <div class="menu-content d-flex">
            <div
              v-for="(group, index) in menu"
              :key="index"
              class="menu-label jc-center t-align-center font-16"
              :class="{ actived: index == menu_actived }"
              @click="goto_product(index)"
            >
              {{ group.menu_series_name }}
            </div>
          </div>
        </div>

        <!-- 各分類的飲品清單 -->
        <div
          v-for="(group, index) in menu"
          :key="index"
          :ref="`menu_r${index}`"
          class="contect-container"
          :class="{ contect_end: index === menu.length - 1 }"
        >
          <div class="title font-24">
            <p>{{ group.menu_series_name }}</p>
          </div>

          <div class="row">
            <div
              v-for="(drink, dindex) in group.drink_items"
              :key="dindex"
              class="col-lg-4 col-md-6"
            >
              <div
                ref="product-list"
                class="card card-hover card_image d-flex jc-space"
                :class="{ card_image: drink.drink_image !== null }"
                @click="select_item(drink)"
              >
                <div class="card-body">
                  <div class="d-flex fd-column-full jc-space">
                    <div>
                      <p class="m-b-8 font-18">{{ drink.drink }}</p>
                      <p
                        v-if="drink.drink_describe !== null"
                        class="m-b-8 font-14 t-gray-7 line-2"
                      >
                        {{ drink.drink_describe }}
                      </p>
                    </div>

                    <div class="d-flex">
                      <p class="p-b-8 font-16">NT${{ drink.drink_price }}</p>
                      <p v-if="drink.drink_sell === 'false'" class="m-l-8 font-16">
                        {{ $t('sold_out') }}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <!-- 購物車數量徽章，由 bind_cart() 直接操作 DOM 更新 -->
                  <p :ref="drink.drink_id" class="num-label">0</p>
                  <img
                    v-if="drink.drink_image !== null"
                    :data-src="drink.drink_image"
                    :src="img_src"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="is_loading === true" class="loading">
      <v-preloader />
    </div>

    <v-footer />

    <v-order
      v-if="order_show"
      :is_close="is_close"
      :product="product"
      @event="trigger_event"
    />

    <v-location
      v-if="location_show"
      :user_location="delivery.location"
      @event="trigger_event"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { mapActions, mapGetters } from 'vuex'

import VCart from '@/components/VCart.vue'
import VHeader from '@/components/VHeader.vue'
import VProduct from '@/components/VProduct.vue'
import VFooter from '@/components/VFooter.vue'
import VPreloader from '@/components/VPreloader.vue'
import VLocation from '@/components/VLocation.vue'
import * as types from '@/store/mutation-types'
import { lockScroll, unlockScroll, sleep, getStorage } from '@/utils'

const STORE_LOCATION = { lat: 25.0142841153413, lng: 121.21120369949432 }
const KEY_ROUTES = '5ew41gf56ew456-routes'

/** 備餐時間：每 6 杯加 10 分鐘 */
const CUPS_PER_TIME_STEP = 6
const MINUTES_PER_STEP = 10

const WIDE_SCREEN = 650

export default {
  components: {
    'v-cart': VCart,
    'v-header': VHeader,
    'v-order': VProduct,
    'v-footer': VFooter,
    'v-preloader': VPreloader,
    'v-location': VLocation
  },

  data() {
    return {
      is_close: this.getClose(),
      is_loading: false,
      header_show: false,
      order_show: false,
      cart_show: false,
      is_cart_show: false,
      menu_actived: -1,
      menu: this.getProduct(),
      product: null,
      is_btn_show: false,
      total_price: 0,
      total_count: 0,
      time: 10,
      select_id: null,
      cart_list: this.getCartList(),
      img_src: 'default.jpg',
      observer: null,
      order_type: this.getOrderType(),
      location_show: false,

      delivery: {
        location: this.getLocation(),
        start_location: { ...STORE_LOCATION },
        end_location: null,
        center: { ...STORE_LOCATION },
        distance: 0,
        fee: 0,
        time: 0,
        place: null
      }
    }
  },

  computed: {
    cart_actived() {
      return !this.is_close && this.menu !== null
    },

    get_center() {
      return this.delivery.center
    }
  },

  watch: {
    cart_show() {
      // 延遲掛載才不會被開啟購物車的那一次點擊立刻關掉
      if (this.cart_show) {
        setTimeout(() => document.addEventListener('click', this.documentClick), 500)
      } else {
        document.removeEventListener('click', this.documentClick)
      }
    }
  },

  methods: {
    ...mapActions([
      'setProduct',
      'setSelProduct',
      'setScroll',
      'setClientID',
      'setStoreStatus',
      'setOrderType'
    ]),
    ...mapGetters([
      'getProduct',
      'getScroll',
      'getCartList',
      'getClose',
      'getSetupTime',
      'getOrderType',
      'getLocation',
      'getOpenCart'
    ]),

    /** 捲動時找出目前可見的分類，高亮並把標籤列水平捲到該位置 */
    handleScroll() {
      if (this.menu === null || window.scrollY <= 150) {
        this.menu_actived = -1
        this.header_show = false
        return
      }

      this.header_show = true

      let nearest = 100000
      for (let i = 0; i < this.menu.length; i++) {
        const bottom = this.$refs[`menu_r${i}`][0].getBoundingClientRect().bottom
        if (bottom - 120 > 0 && bottom <= nearest) {
          this.menu_actived = i
          nearest = bottom
        }
      }

      const step = window.innerWidth > WIDE_SCREEN ? 100 : 50
      document.getElementsByClassName('menu-container')[0].scrollLeft = step * this.menu_actived
    },

    goto_product(index) {
      const top = this.$refs[`menu_r${index}`][0].getBoundingClientRect().top
      window.scrollTo({ top: window.scrollY + top - 120, left: 0, behavior: 'smooth' })
    },

    select_item_from_cart(item) {
      lockScroll()
      this.product = item
      this.order_show = true
      this.is_cart_show = true
    },

    select_item(item) {
      lockScroll()
      this.product = item
      this.order_show = true
      this.cart_show = false
    },

    /** 點在購物車外就關閉（購物車開著客製化對話框時不處理） */
    documentClick(event) {
      if (this.is_cart_show) return

      const cart = this.$refs.pop_cart
      if (cart === undefined) return

      const target = event.target
      if (cart !== target && !cart.$el.contains(target)) this.cart_show = false
    },

    /**
     * 重算購物車統計，並直接操作 DOM 更新每個飲品卡片上的數量徽章。
     * 原始實作就是走 $refs 改 innerHTML/classList，此處保留同樣做法
     * 以免改變行為。
     */
    bind_cart() {
      if (this.is_close) {
        this.is_btn_show = false
        return
      }

      // 先把所有徽章歸零
      for (const card of this.$refs['product-list']) {
        const badge = card.children[1].children[0]
        if (badge.classList.contains('num-label')) {
          badge.classList.remove('show')
          badge.innerHTML = '0'
        }
      }

      this.total_price = 0
      this.total_count = 0
      this.cart_list = this.getCartList().map((item) => item)

      for (const item of this.cart_list) {
        const badge = this.$refs[item.drink_id][0]
        badge.classList.add('show')
        badge.innerHTML = parseInt(badge.innerHTML) + item.count

        this.total_price += item.total_price
        this.total_count += item.count
      }

      this.time =
        MINUTES_PER_STEP * (Math.floor(this.total_count / CUPS_PER_TIME_STEP) + 1) +
        this.getSetupTime()

      this.is_btn_show = this.cart_list.length > 0
    },

    trigger_cart() {
      this.cart_show = true
    },

    trigger_event(event) {
      switch (event) {
        case 'update_cart':
          this.bind_cart()
          this.order_show = false
          setTimeout(() => (this.is_cart_show = false), 500)
          unlockScroll()
          break

        case 'order_close':
          this.order_show = false
          setTimeout(() => (this.is_cart_show = false), 500)
          unlockScroll()
          break

        case 'cart_close':
          this.cart_show = false
          break

        case 'trigger_cart':
          this.trigger_cart()
          break

        case 'checkout':
          this.setScroll(window.scrollY)
          this.$router.push('/checkout')
          break

        case 'location_close':
          this.location_show = false
          unlockScroll()
          break

        case 'set_location':
          this.location_show = true
          lockScroll()
          break

        case 'location_update':
          this.location_show = false
          unlockScroll()
          this.order_type = this.getOrderType()
          this.update_map()
          break
      }
    },

    update_map() {
      const routes = getStorage(KEY_ROUTES)
      if (routes !== null) this.delivery = routes
    },

    api_response_message(message) {
      this.is_loading = false
      alert(message)
    },

    api_error() {
      this.is_loading = false
      alert(this.$t('err_9000'))
    },

    /** ?d=<drink_id> 指定的商品，載入後自動開啟其客製化對話框 */
    select_produce() {
      for (const group of this.menu) {
        for (const drink of group.drink_items) {
          if (drink.drink_id === this.select_id) {
            this.select_item(drink)
            return
          }
        }
      }
    },

    /** 圖片延遲載入：進入視窗才把 data-src 換到 src */
    bind_lazy_images() {
      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue

          const img = entry.target
          img.setAttribute('src', img.getAttribute('data-src'))
          this.observer.unobserve(img)
        }
      })

      for (const img of document.getElementsByTagName('img')) {
        this.observer.observe(img)
      }
    },

    async apiGetProducts() {
      this.is_loading = true

      try {
        const res = await axios.get('/api/drinkMenu')

        if (res.data.code_status !== 0) {
          this.api_response_message(res.data.response_data.message)
          return
        }

        this.menu = res.data.response_data.menu
        this.setProduct(res.data.response_data.menu)
        this.is_close = this.getClose()
        this.is_loading = false

        // 等 DOM 掛上來才能綁徽章與圖片
        await sleep(500)

        this.bind_cart()
        this.update_map()
        this.cart_show = this.getOpenCart()
        this.bind_lazy_images()

        if (this.select_id !== null) this.select_produce()
      } catch (e) {
        console.log(e)
      }
    },

    async apiGetStoreStatus() {
      try {
        const res = await axios.get('/api/storeStatus')

        if (res.data.code_status === 0) {
          this.setStoreStatus(res.data.response_data)
          this.apiGetProducts()
        } else {
          this.api_response_message()
        }
      } catch (e) {
        this.api_error()
      }
    }
  },

  created() {
    window.addEventListener('scroll', this.handleScroll)

    const url = new URL(window.location.href)

    const clientId = url.searchParams.get('q')
    if (clientId !== null) this.setClientID(clientId)

    this.select_id = url.searchParams.get('d')
  },

  destroyed() {
    window.removeEventListener('scroll', this.handleScroll)
  },

  mounted() {
    window.scrollTo(0, this.getScroll())

    const orderTypeParam = new URL(window.location.href).searchParams.get('o')

    if (orderTypeParam === 'yes') {
      this.order_type = types.ORDER_TYPE_PICK_UP
      this.setOrderType(this.order_type)
      this.apiGetStoreStatus()
      return
    }

    if (orderTypeParam === 'no') {
      // 指定外送但沒有地址，導回首頁讓使用者輸入
      if (this.delivery.location === '') {
        this.$router.push('/')
        return
      }

      this.order_type = types.ORDER_TYPE_DELIVERY
      this.setOrderType(this.order_type)
      this.apiGetStoreStatus()
      return
    }

    this.apiGetStoreStatus()
  }
}
</script>
